import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import SolverTwoPanelLayout from './SolverTwoPanelLayout';
import DropdownSelect from '../Visualeffects/DropdownSelect';

const METHOD_OPTIONS = [
  { value: 'richardson', label: 'Richardson' },
  { value: 'jacobi', label: 'Jacobi' },
  { value: 'forward gauss-seidel', label: 'Forward Gauss–Seidel' },
  { value: 'backward gauss-seidel', label: 'Backward Gauss–Seidel' },
  { value: 'symmetrical gauss-seidel', label: 'Symmetrical Gauss–Seidel' },
  { value: 'ssor', label: 'Symmetric Successive Over-Relaxation (SSOR)' },
  { value: 'cg', label: 'Conjugate Gradient (SPD only)' },
  { value: 'gmres', label: 'GMRES' },
];

const requiresOmega = (method) => method === 'richardson' || method === 'ssor';

const parseMatrix = (input) => {
  if (Array.isArray(input)) {
    return input.map((row) => (Array.isArray(row) ? row.map(Number) : [Number(row)]));
  }
  if (typeof input !== 'string') return [];
  const trimmed = input.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return parseMatrix(parsed);
  } catch {
    const rows = trimmed.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    return rows.map((row) =>
      row
        .split(/[,\s]+/)
        .filter(Boolean)
        .map((cell) => Number(cell))
    );
  }
};

const parseVector = (input) => {
  if (Array.isArray(input)) return input.map(Number);
  if (typeof input !== 'string') return [];
  const trimmed = input.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return parseVector(parsed);
  } catch {
    return trimmed
      .split(/[,\s]+/)
      .filter(Boolean)
      .map((cell) => Number(cell));
  }
};

const formatArray = (arr = [], digits = 6) =>
  Array.isArray(arr)
    ? `[${arr.map((v) => (Number.isFinite(v) ? Number(v).toFixed(digits) : String(v))).join(', ')}]`
    : String(arr ?? '');

const parseNumericInput = (value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return Number.NaN;
    }
    return Number(trimmed);
  }
  return Number(value);
};

export default function IterativeLinearSolversUI({ state = {}, setState }) {
  const {
    matrixInput = '',
    vectorInput = '',
    method = 'jacobi',
    maxIterations = '',
    tolerance = '',
    omega = '',
    loading = false,
    result = null,
    error = null,
    matrixError = false,
    vectorError = false,
    omegaError = false,
    initialVectorInput = '',
    initialVectorError = false,
  } = state;

  const set = (key, value) => setState((prev) => ({ ...prev, [key]: value }));

  const [showAllSteps, setShowAllSteps] = React.useState(false);

  const isRichardson = method === 'richardson';
  const dampingLabel = isRichardson ? 'α (damping)' : 'ω (damping)';
  const dampingErrorText = method === 'ssor'
    ? 'Choose ω ≠ 0 and ω ≠ 2.'
    : 'Choose a non-zero damping factor α.';

  React.useEffect(() => {
    setShowAllSteps(false);
  }, [result]);

  const handleSolve = async () => {
    setShowAllSteps(false);
    set('loading', true);
    set('error', null);
    set('matrixError', false);
    set('vectorError', false);
    set('omegaError', false);
    set('initialVectorError', false);

    let matrix;
    let vector;
    let initialVector = null;

    try {
      matrix = parseMatrix(matrixInput);
      if (!matrix.length || matrix.some((row) => row.length !== matrix[0].length)) {
        throw new Error('Matrix must be rectangular.');
      }
      if (matrix.some((row) => row.some((value) => !Number.isFinite(value)))) {
        throw new Error('Matrix entries must be numeric.');
      }
    } catch (err) {
      set('loading', false);
      set('error', err.message || 'Unable to parse matrix A.');
      set('matrixError', true);
      return;
    }

    try {
      vector = parseVector(vectorInput);
      if (vector.length !== matrix.length) {
        throw new Error('Vector length must equal number of matrix rows.');
      }
      if (vector.some((value) => !Number.isFinite(value))) {
        throw new Error('Vector entries must be numeric.');
      }
    } catch (err) {
      set('loading', false);
      set('error', err.message || 'Unable to parse vector b.');
      set('vectorError', true);
      return;
    }

    try {
      const trimmed = typeof initialVectorInput === 'string' ? initialVectorInput.trim() : '';
      if (trimmed) {
        initialVector = parseVector(initialVectorInput);
        if (initialVector.length !== matrix.length) {
          throw new Error('Initial vector length must equal number of matrix rows.');
        }
        if (initialVector.some((value) => !Number.isFinite(value))) {
          throw new Error('Initial vector entries must be numeric.');
        }
      }
    } catch (err) {
      set('loading', false);
      set('error', err.message || 'Unable to parse initial vector x₀.');
      set('initialVectorError', true);
      return;
    }

    const maxIterValue = parseNumericInput(maxIterations);
    if (!Number.isFinite(maxIterValue) || maxIterValue <= 0) {
      set('loading', false);
      set('error', 'Maximum iterations must be a positive number.');
      return;
    }

    const toleranceValue = parseNumericInput(tolerance);
    if (!Number.isFinite(toleranceValue) || toleranceValue <= 0) {
      set('loading', false);
      set('error', 'Tolerance must be a positive number.');
      return;
    }

    let omegaValue = null;
    if (requiresOmega(method)) {
      omegaValue = parseNumericInput(omega);
      if (!Number.isFinite(omegaValue) || omegaValue === 0 || (method === 'ssor' && omegaValue === 2)) {
        set('loading', false);
        set('error', method === 'ssor'
          ? 'SSOR requires ω ≠ 0 and ω ≠ 2.'
          : 'Richardson requires a non-zero damping parameter α.');
        set('omegaError', true);
        return;
      }
    }

    try {
      const payload = {
        matrix,
        vector,
        method,
        max_iterations: maxIterValue,
        tolerance: toleranceValue,
        ...(requiresOmega(method) ? { omega: omegaValue } : {}),
        ...(initialVector ? { initial_vector: initialVector } : {}),
      };

      const response = await fetch('https://api.numsolve.org/iterlinsolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data?.error) {
        set('error', data.error);
        set('result', null);
      } else {
        set('result', data);
        set('error', null);

        try {
          const logEntry = {
            timestamp: new Date().toISOString(),
            method: 'iterlinsolve',
            input: {
              matrix,
              vector,
              method,
              max_iterations: maxIterValue,
              tolerance: toleranceValue,
              ...(requiresOmega(method) ? { omega: omegaValue } : {}),
              ...(initialVector ? { initial_vector: initialVector } : {}),
            },
            result: {
              solution: data?.solution ?? null,
              iteration_count: data?.iteration_count ?? null,
              converged: data?.converged ?? null,
              final_residual: data?.final_residual ?? null,
            },
            log: Array.isArray(data?.history) ? data.history : null,
            log_file: data?.log_file || null,
          };
          const existing = JSON.parse(localStorage.getItem('calcLogs') || '[]');
          const updated = [logEntry, ...existing].slice(0, 100);
          localStorage.setItem('calcLogs', JSON.stringify(updated));
          window.dispatchEvent(new Event('calcLogsUpdated'));
        } catch {
          // Ignore persistence errors
        }
      }
    } catch (err) {
      set('error', err?.message || 'Backend not reachable.');
      set('result', null);
    }

    set('loading', false);
  };

  const inputCard = (
    <div className="rounded-3xl border border-sky-200 bg-white/85 p-8 shadow-[0_30px_80px_-45px_rgba(56,189,248,0.55)] backdrop-blur">
      <div className="space-y-6">
        <div>
          <label className="mb-2 block text-sm font-semibold text-gray-900">Matrix A</label>
          <div className={`input-wrapper input-pulse-sky ${matrixError ? 'ring-2 ring-red-400' : ''}`}>
            <textarea
              value={matrixInput}
              onChange={(e) => set('matrixInput', e.target.value)}
              placeholder="[[4, 1], [2, 3]]"
              rows={4}
              className={`w-full resize-none rounded-xl border px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 ${
                matrixError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
              }`}
            />
          </div>
          {matrixError && (
            <p className="mt-1 text-xs font-medium text-red-600">Unable to parse matrix. Ensure rows have equal length.</p>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-semibold text-gray-900">Vector b</label>
          <div className={`input-wrapper input-wrapper-tight input-pulse-sky ${vectorError ? 'ring-2 ring-red-400' : ''}`}>
            <input
              type="text"
              value={vectorInput}
              onChange={(e) => set('vectorInput', e.target.value)}
              placeholder="[1, 2]"
              className={`w-full rounded-xl border px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 ${
                vectorError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
              }`}
            />
          </div>
          {vectorError && (
            <p className="mt-1 text-xs font-medium text-red-600">Vector length must match the number of rows in A.</p>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-semibold text-gray-900">Initial vector x₀ (optional)</label>
          <div className={`input-wrapper input-wrapper-tight input-pulse-sky ${initialVectorError ? 'ring-2 ring-red-400' : ''}`}>
            <input
              type="text"
              value={initialVectorInput}
              onChange={(e) => set('initialVectorInput', e.target.value)}
              placeholder="[0, 0]"
              className={`w-full rounded-xl border px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 ${
                initialVectorError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
              }`}
            />
          </div>
          {initialVectorError && (
            <p className="mt-1 text-xs font-medium text-red-600">Initial vector must be numeric and match the dimension of A.</p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <DropdownSelect
              label="Iterative method"
              color="sky"
              value={method}
              onChange={(value) => set('method', value)}
              options={METHOD_OPTIONS}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-900">Maximum iterations</label>
            <input
              type="number"
              min="1"
              value={maxIterations}
              onChange={(e) => set('maxIterations', e.target.value)}
              placeholder="e.g. 100"
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-900">Tolerance</label>
            <input
              type="text"
              value={tolerance}
              onChange={(e) => set('tolerance', e.target.value)}
              placeholder="e.g. 1e-6"
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
          </div>

          {requiresOmega(method) && (
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-900">{dampingLabel}</label>
              <input
                type="text"
                value={omega}
                onChange={(e) => set('omega', e.target.value)}
                placeholder="e.g. 1.0"
                className={`w-full rounded-xl border px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 ${
                  omegaError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
                }`}
              />
              {omegaError && (
                <p className="mt-1 text-xs font-medium text-red-600">{dampingErrorText}</p>
              )}
            </div>
          )}
        </div>

        <div className="pt-4">
          <button
            type="button"
            onClick={handleSolve}
            disabled={loading}
            className="button disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              '--button-base-color': '#38bdf8',
              '--button-hover-color': '#0ea5e9',
            }}
          >
            {loading ? 'Solving…' : 'Solve'}
          </button>
        </div>
      </div>
    </div>
  );

  const history = Array.isArray(result?.history)
    ? result.history
    : Array.isArray(result?.log)
      ? result.log
      : [];

  const truncatedHistory = history.slice(0, 5);
  const displayedHistory = showAllSteps ? truncatedHistory : truncatedHistory.slice(0, 2);
  const hasHiddenHistory = truncatedHistory.length > 2;
  const toleranceParsed = parseNumericInput(tolerance);
  const toleranceNumeric = Number.isFinite(toleranceParsed) ? toleranceParsed : null;
  const residualChartData = history
    .map((entry, index) => {
      const iterationRaw =
        entry?.iteration ?? entry?.step ?? entry?.iter ?? entry?.iteration_index ?? index + 1;
      const iterationNumber = Number.isFinite(Number(iterationRaw))
        ? Number(iterationRaw)
        : index + 1;

      const residualCandidate =
        entry?.residual ?? entry?.residual_norm ?? entry?.error ?? entry?.err ?? entry?.residuum;
      if (residualCandidate === null || residualCandidate === undefined || residualCandidate === '') {
        return null;
      }
      const residualNumber =
        typeof residualCandidate === 'number'
          ? residualCandidate
          : typeof residualCandidate === 'string'
            ? Number(residualCandidate)
            : Number.NaN;

      if (!Number.isFinite(iterationNumber) || !Number.isFinite(residualNumber)) {
        return null;
      }

      if (residualNumber <= 0) {
        return null;
      }

      return {
        iteration: iterationNumber,
        residual: residualNumber,
      };
    })
    .filter(Boolean);
  const residualDomain = React.useMemo(() => {
    if (residualChartData.length === 0) {
      return ['auto', 'auto'];
    }
    const residuals = residualChartData
      .map((entry) => entry.residual)
      .filter((value) => Number.isFinite(value) && value > 0);
    if (residuals.length === 0) {
      return ['auto', 'auto'];
    }
    const minResidual = Math.min(...residuals);
    const maxResidual = Math.max(...residuals);
    if (!Number.isFinite(minResidual) || !Number.isFinite(maxResidual)) {
      return ['auto', 'auto'];
    }
    const lowerPadding = minResidual * 0.1;
    const upperPadding = maxResidual * 2;
    return [lowerPadding, upperPadding];
  }, [residualChartData]);
  const formatResidualTick = (value) => {
    if (!Number.isFinite(value)) return value;
    const abs = Math.abs(value);
    if (abs === 0) return '0';
    if (abs < 1e-4 || abs >= 1e4) {
      return value.toExponential(2);
    }
    return value.toPrecision(3);
  };
  const finalResidualValue =
    result?.final_residual ?? (history.length ? history[history.length - 1]?.residual : undefined);
  const formattedResidual = Number.isFinite(finalResidualValue)
    ? Number(finalResidualValue).toExponential(3)
    : finalResidualValue != null
      ? String(finalResidualValue)
      : 'n/a';
  const iterationCount = result?.iteration_count ?? (Array.isArray(history) ? history.length : undefined);
  const isSuccess = Boolean(result && !error && (result.converged === undefined || result.converged));

  const outputCard = (
    <div
      className={`rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] transition-all duration-300 ${
        !loading && isSuccess ? 'result-success' : ''
      }`}
    >
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Results</h2>

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-800">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-10 text-sky-600">
          <span className="animate-pulse text-base font-semibold">Running iteration…</span>
        </div>
      )}

      {!loading && !result && !error && (
        <div className="text-sm text-gray-500">No results yet. Please enter a matrix.</div>
      )}

      {!loading && result && !error && (
        <div className="space-y-5 text-sm text-gray-800">
          <div className="space-y-2">
            <p>
              <span className="font-semibold text-gray-900">Solution x:</span>{' '}
              {formatArray(result.solution, 6)}
            </p>
            <p>
              <span className="font-semibold text-gray-900">Number of Iterations:</span>{' '}
              {iterationCount !== undefined ? iterationCount : 'n/a'}
            </p>
            <p>
              <span className="font-semibold text-gray-900">Residual:</span> {formattedResidual}
            </p>
          </div>

          {residualChartData.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Residual vs. iteration</h3>
              <div className="mt-3 h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={residualChartData} margin={{ top: 12, right: 12, bottom: 8, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      type="number"
                      dataKey="iteration"
                      allowDecimals={false}
                      tick={{ fontSize: 12, fill: '#1e293b' }}
                      tickFormatter={(value) => `#${value}`}
                      stroke="#94a3b8"
                    />
                    <YAxis
                      type="number"
                      scale="log"
                      allowDataOverflow
                      tick={{ fontSize: 12, fill: '#1e293b' }}
                      tickFormatter={formatResidualTick}
                      stroke="#94a3b8"
                      domain={residualDomain}
                    />
                    <Tooltip
                      formatter={(value) =>
                        Number.isFinite(value) ? Number(value).toExponential(3) : value
                      }
                      labelFormatter={(label) => `Iteration ${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="residual"
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      dot={{ r: 3, strokeWidth: 1, stroke: '#0284c7', fill: '#38bdf8' }}
                      isAnimationActive={false}
                    />
                    {toleranceNumeric !== null && toleranceNumeric > 0 && (
                      <ReferenceLine
                        y={toleranceNumeric}
                        stroke="#38bdf8"
                        strokeDasharray="6 6"
                        label={{ value: 'Tolerance', position: 'top', fill: '#0369a1', fontSize: 12 }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-900">Iteration log</h3>
            {truncatedHistory.length === 0 ? (
              <p className="mt-2 text-xs text-gray-500">No iteration history was provided.</p>
            ) : (
              <>
                <ol className="mt-3 space-y-2 text-xs text-gray-700">
                  {displayedHistory.map((entry, index) => {
                    const iterationLabel =
                      entry?.iteration ?? entry?.step ?? entry?.iter ?? entry?.iteration_index ?? index + 1;
                    const entryResidual = entry?.residual;
                    const formattedEntryResidual = Number.isFinite(entryResidual)
                      ? Number(entryResidual).toExponential(3)
                      : entryResidual != null
                        ? String(entryResidual)
                        : 'n/a';

                    return (
                      <li key={`${iterationLabel}-${index}`} className="leading-relaxed">
                        <span className="font-semibold text-gray-900">Iteration {iterationLabel}:</span>{' '}
                        {entry?.x !== undefined && (
                          <>
                            <span className="font-semibold text-gray-900">x</span> = {formatArray(entry.x, 6)};
                          </>
                        )}
                        {entryResidual !== undefined && (
                          <>
                            {' '}
                            <span className="font-semibold text-gray-900">Residual</span> = {formattedEntryResidual}
                          </>
                        )}
                      </li>
                    );
                  })}
                </ol>
                {hasHiddenHistory && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setShowAllSteps((prev) => !prev)}
                      className="inline-flex items-center rounded-full border border-sky-200 bg-white px-4 py-2 text-xs font-semibold text-sky-700 shadow-sm transition hover:bg-sky-100"
                    >
                      {showAllSteps ? 'Show fewer steps' : 'Show more steps'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <SolverTwoPanelLayout inputCard={inputCard} outputCard={outputCard} loading={loading} />
  );
}
